import { Form, FormikProps, useFormikContext } from 'formik';
import { FormikDebounce } from '../common/form/FormikDebounce';
import { FC, useCallback, useEffect, useRef, useState } from 'react';
import { keyframes } from '@emotion/react';
import { useTranslation } from 'react-i18next';
import { useParams } from 'react-router';
import { useAppDispatch, useAppSelector } from '../../store';
import { getSupplier, getSupplierFromUserId, selectSupplier, selectSupplierState, supplierActions } from '../../store/entities/supplier';
import { supplierListActions } from '../../store/entities/supplierList';
import { QcsMenuItem } from '../common/basic/QcsMenuItem';
import { DetailHeader } from '../common/DetailHeader';
import { FormContainer } from '../common/form/FormContainer';
import { Input } from '../common/form/Input';
import { Select } from '../common/form/Select';
import { QcsSwitch } from '../common/form/QcsSwitch';
import { QcsInfoTooltip } from '../common/basic/QcsInfoTooltip';
import { useBusinessSettings } from '../../hooks/useBusinessSettings';
import { SubmitButton } from '../common/form/SubmitButton';
import * as Yup from 'yup';
import { validations } from '../../utils/validations';
import { selectIdentity } from '../../store/entities/identity';
import { FormGroup } from '../common/form/FormGroup';
import { QcsLoadingButton } from '../common/basic/QcsLoadingButton';
import { SupplierEmployee } from './SupplierEmployee';
import { SupplierCars } from './SupplierCars';
import { SubcontractorLendConfirmDialog, SubcontractorLendConfirmHandle } from './SubcontractorLendConfirmDialog';
import { SubcontractorLendRequestDialog } from './SubcontractorLendRequestDialog';
import { hasRole, isExternal } from '../../utils/roles';
import { QcsBox } from '../common/basic/QcsBox';
import { QcsTypography } from '../common/basic/QcsTypography';
import { formatChangeInfo, joinStrings } from '../../utils/format';
import { Box, Paper, Tab, Tooltip, useTheme } from '@mui/material';
import { TabContext, TabPanel } from '@mui/lab';
import { QcsTabs } from '../common/basic/QcsTabs';
import WarningAmberRoundedIcon from '@mui/icons-material/WarningAmberRounded';
import { RepairLineBreaks } from '../common/RepairLineBreaks';
import { SubmitAndNewButtons } from '../common/form/SubmitAndNewButtons';

import { SupplierDto } from '@api/models/suppliers/SupplierDto';
import { ChangeInfo } from '@api/models/suppliers/ChangeInfo';
import { PathKey, useNavigation } from '../../utils/urlPaths';
import { sectionLabelSx } from '../invitation/Detail/styles';
import { aresService, supplierEmployeeService, supplierService } from '@api/services/services';
import { ShowError } from '../ShowError';
import { ShowResourceContent } from '../ShowResourceContent';
import { resources } from '@api/resources';
import { SupplierState } from '@api/models/enums/SupplierState';
import { UserRole } from '@api/models/enums/UserRole';
import { UserState } from '@api/models/enums/UserState';

interface SupplierDtoWithButton extends SupplierDto {
  addEmployeeMode?: boolean;
  saveAndNew?: boolean;
}

// Briefly pulses a background highlight to draw the user's eye to a field.
const highlightPulse = keyframes`
  0%, 100% { background-color: transparent; }
  50% { background-color: rgba(255, 167, 38, 0.25); }
`;

const SupplierDetailEmployeeModeButton: FC = () => {
  const { t } = useTranslation();
  const { isSubmitting, setFieldValue, submitForm } = useFormikContext<SupplierDtoWithButton>();

  const handleClick = async () => {
    setFieldValue('addEmployeeMode', true);
    try {
      await submitForm();
    } finally {
      setFieldValue('addEmployeeMode', false);
    }
  };

  return (
    <QcsLoadingButton variant="contained" disabled={isSubmitting} loading={isSubmitting} onClick={handleClick}>
      {t('supplier.addWithEmployee')}
    </QcsLoadingButton>
  );
};

interface SupplierDetailProps {
  createNew?: boolean;
  onSaved?: (createdSupplier?: SupplierDto) => void;
  // When set, the component is embedded (e.g. in a drawer): it shows this supplier
  // directly instead of reading the id from the URL, and tabs switch locally
  // without navigating the underlying page.
  forSupplierId?: string;
}

export const SupplierDetail: FC<SupplierDetailProps> = ({ createNew, onSaved, forSupplierId } = {}) => {
  const { t } = useTranslation();
  const theme = useTheme();
  const params = useParams<{ supplierId: string; tabId?: string }>();
  const identity = useAppSelector(selectIdentity);
  const businessSettings = useBusinessSettings();

  // Helper function to parse route params based on user role
  const parseRouteParams = () => {
    const isExternalManager = hasRole(identity.roles, [UserRole.ExternalManager]);
    const tabNames = ['detail', 'employees', 'cars'];

    // For ExternalManager: route is /supplier/:tabId (e.g., /supplier/detail)
    // For others: route is /supplier/:supplierId/:tabId (e.g., /supplier/123/detail)
    if (isExternalManager && params.supplierId && tabNames.includes(params.supplierId)) {
      return {
        supplierId: undefined,
        tabId: params.supplierId,
      };
    }

    return {
      supplierId: params.supplierId,
      tabId: params.tabId || 'detail',
    };
  };

  const { supplierId: routeSupplierId, tabId } = parseRouteParams();
  const embedded = forSupplierId !== undefined;
  const supplierId = createNew ? 'new' : embedded ? forSupplierId : routeSupplierId;
  const isExternalUser = hasRole(identity.roles, [UserRole.ExternalManager, UserRole.ExternalWorker]);
  const [activeTab, setActiveTab] = useState('detail');
  const { navigate, getPath, goBack, getBackPath: backTo } = useNavigation('/supplier');
  const supplier = useAppSelector(selectSupplier);
  const dispatch = useAppDispatch();
  const [hasRequestedEmployees, setHasRequestedEmployees] = useState(false);
  const [lendRequestNoticeDismissedForId, setLendRequestNoticeDismissedForId] = useState<string | undefined>(undefined);
  const [highlightLendSwitch, setHighlightLendSwitch] = useState(false);
  const formikRef = useRef<FormikProps<SupplierDtoWithButton> | null>(null);
  const lendConfirmRef = useRef<SubcontractorLendConfirmHandle>(null);
  const lendSwitchRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (supplierId === 'new') {
      dispatch(supplierActions.default());
    } else if (!embedded && hasRole(identity.roles, [UserRole.ExternalManager])) {
      dispatch(getSupplierFromUserId(identity.id));
    } else {
      dispatch(getSupplier(supplierId!));
    }
  }, [supplierId, dispatch, identity.roles, identity.id, embedded]);

  // Flag the employees tab when some supplier employees are still in the REQUESTED
  // state (waiting to be added). There is no such field on the supplier itself, so we
  // ask the backend for a single requested employee and only care whether any exist.
  const fetchHasRequestedEmployees = useCallback(async (): Promise<boolean> => {
    const res = await supplierEmployeeService
      .query({ supplierId: supplier.id!, state: [UserState.Requested], size: 1 }, { silentSuccess: true })
      .catch(() => null);
    return (res?.data.totalElements ?? 0) > 0;
  }, [supplier.id]);

  useEffect(() => {
    // 'new' renders the basic-info form without tabs, so the flag is never read there.j
    if (supplierId === 'new' || !supplier.id) return;

    let active = true;
    fetchHasRequestedEmployees().then((result) => {
      if (active) setHasRequestedEmployees(result);
    });

    return () => {
      active = false;
    };
  }, [supplierId, supplier.id, fetchHasRequestedEmployees]);

  // Notify a company user (not the supplier itself) opening this supplier's card that
  // another supplier has requested activation of "canLendEmployees" for them. Derived
  // directly from already-loaded data rather than an effect; "dismissed" is tracked per
  // supplier id so the notice reappears when a different supplier's card is opened.
  const showLendRequestNotice =
    supplierId !== 'new' &&
    !!supplier.id &&
    !isExternalUser &&
    lendRequestNoticeDismissedForId !== supplier.id &&
    (supplier.subcontractorRequests ?? []).length > 0;

  const handleCloseLendRequestNotice = () => {
    setLendRequestNoticeDismissedForId(supplier.id);
    setHighlightLendSwitch(true);
    lendSwitchRef.current?.scrollIntoView({ behavior: 'smooth', block: 'center' });
  };

  // Redirect to /detail tab if no tabId is present in the URL
  useEffect(() => {
    if (embedded) return;
    const hasTabIdInUrl = params.tabId || (isExternal(identity) && params.supplierId && ['detail', 'employees', 'cars'].includes(params.supplierId));

    if (supplierId !== 'new' && !hasTabIdInUrl) navigate(PathKey.SupplierDetail, { supplierId: supplierId! });
  }, [supplierId, params.supplierId, params.tabId, navigate, identity.roles, embedded]);

  const handleSubmit = async (data: SupplierDtoWithButton) => {
    const activatingLend = !supplier.canLendEmployees && !!data.canLendEmployees;
    let redirectToRequests = false;

    // Enabling "lend employees" while there are pending subcontractor requests also
    // activates the requested employees — warn and let the user confirm or jump to them.
    // Only warn while those employees are still in the REQUESTED state; once activated
    // there is nothing to confirm.
    if (activatingLend && hasRequestedEmployees && (supplier.subcontractorRequests ?? []).length > 0) {
      const action = await lendConfirmRef.current!.confirm();
      if (action === 'cancel') return;
      redirectToRequests = action === 'redirect';
    }

    let newSupplierResponse;
    if (supplierId === 'new') {
      newSupplierResponse = await supplierService.create(data);
    } else {
      await supplierService.update(supplier.id!, data);
      dispatch(getSupplier(supplier.id!));
      // Confirming the lend dialog activates the requested employees server-side, so
      // re-check once the save lands to keep the employees-tab warning icon in sync.
      setHasRequestedEmployees(await fetchHasRequestedEmployees());
    }

    dispatch(supplierListActions.reload(true));

    if (redirectToRequests) {
      lendConfirmRef.current?.goToRequests();
      return;
    }

    if (data.saveAndNew) {
      formikRef.current?.resetForm();
    } else if (data.addEmployeeMode && newSupplierResponse) {
      const newSupplierId = newSupplierResponse.data.id!;
      navigate(
        PathKey.SupplierEmployeeNew,
        { supplierId: newSupplierId },
        { backTo: getPath(PathKey.SupplierEmployees, { supplierId: newSupplierId }) },
      );
    } else if (onSaved) {
      onSaved(newSupplierResponse ? { ...data, id: newSupplierResponse.data.id } : undefined);
    } else if (supplierId === 'new') {
      goBack();
    }
  };

  const renderBasicInfo = () => {
    const subcontractorFields = (['canBorrowEmployees', 'canLendEmployees'] as const).filter((field) => supplier[field]);

    if (isExternalUser) {
      return (
        <QcsBox
          sx={{
            pb: 3,
            display: 'grid',
            gridTemplateColumns: { xs: 'auto', sm: 'auto max-content' },
            gridTemplateAreas: {
              xs: '"buttons" "title"',
              sm: '"title buttons"',
            },
            gap: '2rem',
            width: '100%',
          }}
        >
          <QcsBox sx={{ gridArea: 'title' }}>
            {/* Dodavatel */}
            <QcsTypography variant="subtitle1">
              <strong>{`${t('supplier.detailTitle')}:`}</strong> {supplier.name}, <strong>{`${t('supplier.ico')}:`}</strong> {supplier.ico},{' '}
              <strong>{t('supplier.vat')}:</strong> {supplier.vat}
            </QcsTypography>
            {/* Adresa dodavatele */}
            <QcsTypography variant="subtitle1">
              <strong>{`${t('address.title')}:`}</strong>{' '}
              {joinStrings(supplier.address.street, supplier.address.city, supplier.address.postalCode, supplier.address.country)}
            </QcsTypography>
            {businessSettings?.allowSubcontractors &&
              subcontractorFields.map((field) => (
                <Paper key={field} variant="outlined" sx={{ mt: 2, p: 2, borderRadius: 2, borderColor: 'divider' }}>
                  <QcsTypography variant="body2" sx={sectionLabelSx}>
                    {t(`supplier.${field}ExternalTitle`)}
                  </QcsTypography>
                  <QcsTypography variant="body2" sx={{ mt: 0.5, color: 'text.secondary' }}>
                    <RepairLineBreaks text={t(`supplier.${field}ExternalInfo`, { companyName: businessSettings?.companyName })} />
                  </QcsTypography>
                </Paper>
              ))}
            {supplier.note && (
              <>
                <QcsTypography variant="subtitle1">
                  <strong>{`${t('supplier.note')}:`}</strong>
                </QcsTypography>
                <QcsTypography variant="body1" sx={{ lineHeight: '1.2em' }}>
                  <RepairLineBreaks text={supplier.note} />
                </QcsTypography>
              </>
            )}
          </QcsBox>
          <QcsBox
            sx={{
              gridArea: 'buttons',
              display: 'flex',
              flexDirection: 'column',
              gap: '1rem',
              textAlign: 'center',
            }}
          >
            <QcsTypography variant="subtitle1" sx={{ color: theme.palette.primary.main }}>
              <strong>{t(`entityState.${supplier.state}`)}</strong>
            </QcsTypography>
          </QcsBox>
        </QcsBox>
      );
    }

    return (
      <FormikDebounce<SupplierDtoWithButton>
        initialValues={supplier}
        validationSchema={Yup.object().shape({
          name: validations.stringRequired(t),
          ico: validations.companyNoRequired(t),
          vat: validations.companyVatNoOptional(t),
          address: Yup.object({
            street: validations.stringRequired(t),
            city: validations.stringRequired(t),
            postalCode: validations.postalCodeRequired(t),
            country: validations.stringRequired(t),
          }),
        })}
        onSubmit={handleSubmit}
        innerRef={formikRef}
        enableReinitialize
      >
        {({ values, setValues }) => {
          const handleLoadFromAres = async () => {
            const res = await aresService.fetchSubjectDetailsFromAres(values.ico!);
            const data = Object.fromEntries(Object.entries(res.data).filter(([, value]) => value !== null));
            await setValues({ ...values, ...data });
          };

          return (
            <Form>
              <FormContainer>
                <QcsBox
                  sx={{
                    display: 'grid',
                    gridTemplateColumns: {
                      xs: 'auto',
                      sm: 'auto max-content',
                    },
                    gridTemplateRows: { xs: 'auto auto', sm: 'auto' },
                    rowGap: { xs: supplierId === 'new' ? '1rem' : 0, sm: 0 },
                    columnGap: {
                      xs: 0,
                      sm: supplierId === 'new' ? '1rem' : 0,
                    },
                  }}
                >
                  <Input name="ico" label={t('supplier.ico')} maxLength={14} required removeSpaces />
                  {supplierId === 'new' && (
                    <QcsLoadingButton
                      onClick={handleLoadFromAres}
                      disabled={!values.ico}
                      resourceLoading={resources.fetchSubjectDetailsFromAres}
                      variant="contained"
                      sx={{ height: '3.5rem' }}
                    >
                      {t('admin.company.loadFromAres')}
                    </QcsLoadingButton>
                  )}
                </QcsBox>
                <ShowError resource={resources.fetchSubjectDetailsFromAres} />

                <Input name="name" label={t('supplier.name')} maxLength={100} required />
                <Input name="vat" label={t('supplier.vat')} maxLength={14} />

                <FormGroup name="address.title">
                  <Input name="address.street" label={t('address.street')} maxLength={100} required />
                  <Input name="address.city" label={t('address.city')} maxLength={100} required />
                  <Input name="address.postalCode" label={t('address.postalCode')} maxLength={128} required />
                  <Input name="address.country" label={t('address.country')} maxLength={100} required />
                </FormGroup>

                <Select name="state" label={t('supplier.state')} renderValue={(value) => t(`entityState.${value}`)}>
                  <QcsMenuItem value={SupplierState.Active}>{t('entityState.ACTIVE')}</QcsMenuItem>
                  <QcsMenuItem value={SupplierState.Inactive}>{t('entityState.INACTIVE')}</QcsMenuItem>
                </Select>
                <Input name="note" label={t('supplier.note')} maxLength={250} multiline={true} rows={4} />

                {businessSettings?.allowSubcontractors &&
                  ['canBorrowEmployees', 'canLendEmployees'].map((field) => {
                    const changeInfo = (values as SupplierDto)[`${field}Changed` as keyof SupplierDto] as ChangeInfo | undefined;
                    const isHighlightedLendSwitch = field === 'canLendEmployees' && highlightLendSwitch;
                    return (
                      <Box
                        key={field}
                        ref={field === 'canLendEmployees' ? lendSwitchRef : undefined}
                        onAnimationEnd={isHighlightedLendSwitch ? () => setHighlightLendSwitch(false) : undefined}
                        sx={{
                          borderRadius: 1,
                          ...(isHighlightedLendSwitch && { animation: `${highlightPulse} 1s ease-in-out 3` }),
                        }}
                      >
                        <QcsSwitch
                          name={field}
                          label={
                            <Box component="span" sx={{ display: 'inline-flex', flexDirection: 'column' }}>
                              <Box component="span" sx={{ display: 'inline-flex', alignItems: 'center', gap: 0.5 }}>
                                {t(`supplier.${field}`)}
                                <QcsInfoTooltip
                                  title={t(`supplier.${field}Info`, { supplierName: values.name, companyName: businessSettings?.companyName })}
                                />
                              </Box>
                              {changeInfo?.at && (
                                <Box component="span" sx={{ fontSize: '0.75rem', color: 'text.secondary' }}>
                                  {t('common.lastChange')}: {formatChangeInfo(changeInfo)}
                                </Box>
                              )}
                            </Box>
                          }
                        />
                      </Box>
                    );
                  })}

                {onSaved ? (
                  <SubmitButton />
                ) : (
                  <SubmitAndNewButtons id={supplierId} errorResource={[resources.createSupplier, resources.updateSupplier]}>
                    {supplierId === 'new' && <SupplierDetailEmployeeModeButton />}
                  </SubmitAndNewButtons>
                )}

                {(values.created?.at || values.lastModified?.at) && (
                  <QcsBox sx={{ mt: 2 }}>
                    {values.created?.at && (
                      <QcsTypography variant="caption" sx={{ display: 'block', color: 'text.secondary' }}>
                        {t(values.state === SupplierState.Requested ? 'supplier.requested' : 'supplier.created')}: {formatChangeInfo(values.created)}
                      </QcsTypography>
                    )}
                    {values.lastModified?.at && (
                      <QcsTypography variant="caption" sx={{ display: 'block', color: 'text.secondary' }}>
                        {t('supplier.lastModified')}: {formatChangeInfo(values.lastModified)}
                      </QcsTypography>
                    )}
                  </QcsBox>
                )}
              </FormContainer>
            </Form>
          );
        }}
      </FormikDebounce>
    );
  };

  const handleActiveTab = (_event: React.SyntheticEvent, newValue: string) => {
    if (embedded) {
      setActiveTab(newValue);
    } else if (isExternal(identity)) {
      navigate(`/supplier/${newValue}`);
    } else {
      navigate(`/supplier/${supplierId}/${newValue}`);
    }
  };

  return (
    <ShowResourceContent fetchStateSelector={selectSupplierState} resource={[resources.getSupplierFromUserId, resources.getSupplier]}>
      <SubcontractorLendConfirmDialog ref={lendConfirmRef} requests={supplier.subcontractorRequests ?? []} supplierId={supplier.id!} />
      <SubcontractorLendRequestDialog
        open={showLendRequestNotice && (embedded ? activeTab : tabId) === 'detail'}
        onClose={handleCloseLendRequestNotice}
        requests={supplier.subcontractorRequests ?? []}
      />
      {!onSaved && !embedded && <DetailHeader title={supplier.name} backTo={backTo} />}
      {supplierId === 'new' ? (
        renderBasicInfo()
      ) : (
        <QcsBox>
          <TabContext value={embedded ? activeTab : tabId}>
            <QcsBox>
              <QcsTabs onChange={handleActiveTab}>
                <Tab label={t('supplier.tabs.basicInfo')} value="detail" />
                <Tab
                  label={
                    hasRequestedEmployees ? (
                      <Box sx={{ display: 'inline-flex', alignItems: 'center', gap: 0.5 }}>
                        {t('supplier.tabs.employees')}
                        <Tooltip title={t('supplier.tabs.employeesRequestedWarning')} arrow>
                          <WarningAmberRoundedIcon fontSize="small" sx={{ color: 'warning.main' }} />
                        </Tooltip>
                      </Box>
                    ) : (
                      t('supplier.tabs.employees')
                    )
                  }
                  value="employees"
                />
                <Tab label={t('supplier.tabs.cars')} value="cars" />
              </QcsTabs>
            </QcsBox>
            <TabPanel sx={{ paddingX: { xs: 0, sm: 2 } }} value="detail">
              {renderBasicInfo()}
            </TabPanel>
            <TabPanel sx={{ paddingX: { xs: 0, sm: 2 } }} value="employees">
              <SupplierEmployee supplierId={supplier.id!} />
            </TabPanel>
            <TabPanel sx={{ paddingX: { xs: 0, sm: 2 } }} value="cars">
              <SupplierCars supplierId={supplier.id!} />
            </TabPanel>
          </TabContext>
        </QcsBox>
      )}
    </ShowResourceContent>
  );
};
